import { Link } from "react-router-dom";
import PaymentMethods from "@/components/PaymentMethods";
import { MapPin, Phone, Clock, Mail, Globe, Lock, Facebook, Instagram, Linkedin, Youtube, Music2, Twitter, Link2, MessageCircle, Send, Ghost, AtSign, Pin } from "lucide-react";
import { useSettings, mediaUrl } from "@/context/SettingsContext";
import { useLanguage } from "@/context/LanguageContext";

const Footer = () => {
  const { t } = useLanguage();
  const { settings: s } = useSettings();
  const EXTRA_ICONS = { whatsapp: MessageCircle, telegram: Send, snapchat: Ghost, threads: AtSign, pinterest: Pin };
  const SOCIAL = [["facebook", Facebook], ["instagram", Instagram], ["linkedin", Linkedin], ["tiktok", Music2], ["youtube", Youtube], ["x", Twitter]].filter(([k]) => s.social[k]).map(([k, Icon]) => [k, Icon, s.social[k]])
    .concat((s.social_links || []).filter((l) => l.url).map((l, i) => [l.id || `lien-${i}`, EXTRA_ICONS[l.id] || Link2, l.url, l.label]));
  const PHONES = (s.phones?.length ? s.phones : (s.phone2 ? [{ label: "", number: s.phone2 }] : []));

  return (
    <footer className="relative overflow-hidden bg-forest text-white" data-testid="site-footer">
      <div className="h-1.5 w-full bg-gradient-to-r from-gold via-gold to-brand" />
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="mb-14 overflow-hidden">
          <p className="font-display whitespace-nowrap text-[11vw] font-extrabold leading-none tracking-tight text-white/10 sm:text-[7.5vw]">
            AUTO LUXE
          </p>
        </div>

        <div className="grid gap-10 md:grid-cols-5">
          <div className="md:col-span-2">
            <div className="inline-block rounded-2xl bg-white p-4">
              <img src={mediaUrl(s.logo_full_url)} alt={s.company_name} className="h-24 w-auto" data-testid="footer-logo-image" />
            </div>
            <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-gold">
              {s.slogan}
            </p>
            {SOCIAL.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-3" data-testid="footer-social-links">
                {SOCIAL.map(([k, Icon, url, label]) => (
                  <a key={k} href={url} target="_blank" rel="noopener noreferrer" aria-label={label || k} title={label || k} data-testid={`footer-social-${k}`} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-all hover:-translate-y-0.5 hover:bg-gold">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-gold">
              {t.footer.navTitle}
            </h4>
            <ul className="space-y-2.5 text-sm text-white/80">
              <li><Link to="/" className="transition-colors hover:text-gold">{t.nav.home}</Link></li>
              <li><Link to="/services" className="transition-colors hover:text-gold">{t.nav.services}</Link></li>
              {s.sections?.history !== false && <li><Link to="/histoire" data-testid="footer-history-link" className="transition-colors hover:text-gold">{t.nav.history}</Link></li>}
              <li><Link to="/galerie" className="transition-colors hover:text-gold">{t.nav.gallery}</Link></li>
              <li><Link to="/contact" className="transition-colors hover:text-gold">{t.nav.contact}</Link></li>
              {s.sections?.nav_news !== false && <li><Link to="/actualites" data-testid="footer-news-link" className="transition-colors hover:text-gold">{t.nav.news}</Link></li>}
              <li><Link to="/suivi" data-testid="footer-track-link" className="transition-colors hover:text-gold">{t.nav.track}</Link></li>
              <li><Link to="/mon-espace" data-testid="footer-client-link" className="transition-colors hover:text-gold">{t.client.title}</Link></li>
              {s.sections?.recruit !== false && <li><Link to="/recrutement" data-testid="footer-recruit-link" className="transition-colors hover:text-gold">{t.recruit.nav}</Link></li>}
              {s.sections?.nav_vehicles !== false && <li><Link to="/vehicules" data-testid="footer-vehicles-link" className="transition-colors hover:text-gold">{t.vehicles.nav}</Link></li>}
              {s.sections?.footer_admin !== false && (
              <li>
                <Link to="/admin" data-testid="footer-admin-link" className="inline-flex items-center gap-1.5 transition-colors hover:text-gold">
                  <Lock className="h-3 w-3" />
                  {t.footer.admin}
                </Link>
              </li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-gold">
              {t.footer.activitiesTitle}
            </h4>
            <ul className="space-y-2.5 text-sm text-white/80">
              {t.activities.items.map((a) => (
                <li key={a.title}>{a.title}</li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-gold">
              {t.footer.contactTitle}
            </h4>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                {s.address}
              </li>
              <li className="flex items-start gap-2.5" data-testid="footer-phone-main">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span className="leading-tight">
                  <a href={`tel:${(s.phone || "").replace(/\s/g, "")}`} className="block whitespace-nowrap font-semibold text-white transition-colors hover:text-gold">{s.phone}</a>
                  <span className="block text-[11px] uppercase tracking-wider text-white/50">WhatsApp</span>
                </span>
              </li>
              {PHONES.map((p, i) => (
                <li key={i} className="flex items-start gap-2.5" data-testid={`footer-phone-${i}`}>
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span className="leading-tight">
                    <a href={`tel:${p.number.replace(/\s/g, "")}`} className="block whitespace-nowrap font-semibold text-white transition-colors hover:text-gold">{p.number}</a>
                    {p.label && <span className="block text-[11px] uppercase tracking-wider text-white/50">{p.label}</span>}
                  </span>
                </li>
              ))}
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0 text-gold" />
                <a href={`mailto:${s.email}`} className="transition-colors hover:text-gold">{s.email}</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Globe className="h-4 w-4 shrink-0 text-gold" />
                <a href={`https://${s.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-gold">{s.website}</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Clock className="h-4 w-4 shrink-0 text-gold" />
                {s.hours}
              </li>
            </ul>
            <div className="mt-6"><PaymentMethods compact testid="footer-payment-methods" /></div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/15 pt-6 sm:flex-row">
          <p className="font-mono text-[11px] uppercase tracking-widest text-white/60">
            © {new Date().getFullYear()} {s.company_name} · {t.footer.rights}
          </p>
          <p className="font-mono text-[11px] uppercase tracking-widest text-white/60">
            Abidjan, Côte d'Ivoire
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
